import React, { useRef, useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import { getFirestore } from "@firebase/firestore";
import { app } from '../firebase.js';
import { collection, doc, getDocs, updateDoc, Timestamp } from "@firebase/firestore";
import Form from 'react-bootstrap/Form';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import defaulProfPic from '../Images/defprofile.png'
import MaskedPhone from '../Components/MaskedPhone';
import { logAdminActivity } from '../utils/activityLogger';
import { isPaidAdmissionFeeButNotJoined, normalizeAdmissionStatus } from '../utils/admissions';
import { toDateValue } from '../utils/financeWorkflow';
import './applications.css';

const WEEKLY_TARGET = 5;
const MONTHLY_TARGET = 20;
const JOINED_STATUSES = new Set([
  'student',
  'active',
  'completed',
  'paused',
  'deferred',
  'dropped',
  'expelled',
  'joined'
]);

const formatNumber = (value) => Number(value || 0).toLocaleString('en-KE');

const formatPercent = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '0%';
  }

  return `${value.toFixed(digits)}%`;
};

const getApplicationDate = (student = {}) => (
  toDateValue(student.appDate || student.createdAt || student.admissionDate || student.classStartDate)
);

const getAdmissionDate = (student = {}) => (
  toDateValue(student.admissionDate || student.admissionFeePaidAt || student.classStartDate || student.createdAt || student.appDate)
);

const isJoinedStudent = (student = {}) => {
  const status = normalizeAdmissionStatus(student.userStatus || student.Status || student.status || '');
  return JOINED_STATUSES.has(status);
};

const getWeekBounds = (dateValue = new Date()) => {
  const start = new Date(dateValue);
  const dayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dayOffset);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const getMonthBounds = (dateValue = new Date()) => ({
  start: new Date(dateValue.getFullYear(), dateValue.getMonth(), 1, 0, 0, 0, 0),
  end: new Date(dateValue.getFullYear(), dateValue.getMonth() + 1, 0, 23, 59, 59, 999)
});

const getYearBounds = (dateValue = new Date()) => ({
  start: new Date(dateValue.getFullYear(), 0, 1, 0, 0, 0, 0),
  end: new Date(dateValue.getFullYear(), 11, 31, 23, 59, 59, 999)
});

function AppComp() {

  const db = getFirestore(app);
  let navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [studentsList, setStudentList] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [countAllData, setcountAllData ] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('All');
  const [filterCountry, setFilterCountry] = useState('All');
  const lastSearchRef = useRef('');
  const lastCourseRef = useRef(filterCourse);
  const lastCountryRef = useRef(filterCountry);
  const leadStatuses = ['Neutral', 'Hot', 'Cold'];

  useEffect(() => {
    const fetchData = async () => {
        try {
          setLoading(true);
          const querySnapshot = await getDocs(collection(db, 'users'));
          const studentsItem = [];
          querySnapshot.forEach((doc) => {
              studentsItem.push({ id: doc.id, ...doc.data() });
          });

          const visibleStudents = studentsItem
            .filter((student) => normalizeAdmissionStatus(student.userStatus || student.Status || student.status || '') === 'inactive')
            .filter((student) => !isPaidAdmissionFeeButNotJoined(student))
            .sort((first, second) => {
              const firstTime = getApplicationDate(first)?.getTime?.() || 0;
              const secondTime = getApplicationDate(second)?.getTime?.() || 0;
              return secondTime - firstTime;
            });

          setAllStudents(studentsItem);
          setStudentList(visibleStudents);
          setcountAllData(visibleStudents.length);
        } catch (error) {
          console.error('Failed to load applications:', error);
          setAllStudents([]);
          setStudentList([]);
          setcountAllData(0);
        } finally {
          setLoading(false);
        }

    };

    fetchData();
    }, [db]);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;
      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Applications search: "${trimmed}"`,
          meta: { page: 'Applications', query: trimmed }
        });
      }
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (filterCourse === lastCourseRef.current) return;
    lastCourseRef.current = filterCourse;
    logAdminActivity({
      action: 'Filter',
      details: `Applications course filter: ${filterCourse}`,
      meta: { page: 'Applications', filter: 'course', value: filterCourse }
    });
  }, [filterCourse]);

  useEffect(() => {
    if (filterCountry === lastCountryRef.current) return;
    lastCountryRef.current = filterCountry;
    logAdminActivity({
      action: 'Filter',
      details: `Applications country filter: ${filterCountry}`,
      meta: { page: 'Applications', filter: 'country', value: filterCountry }
    });
  }, [filterCountry]);


  function viewStudent(userId, studentName) {
    logAdminActivity({
      action: 'View Student',
      details: `Opened student profile: ${studentName || 'student'}`,
      meta: { page: 'Applications', studentId: userId, studentName: studentName || '' }
    });
    navigate('/view-student', { state: { myData: userId } });
  }

  const normalizeLeadStatus = (status) => {
    return String(status || 'Neutral').toLowerCase().replace(/\s+/g, '-');
  };

  const updateLeadStatus = async (studentId, studentName, nextStatus) => {
    try {
      await updateDoc(doc(db, 'users', studentId), {
        leadStatus: nextStatus,
        leadStatusUpdatedAt: Timestamp.now()
      });
      setStudentList((prev) =>
        prev.map((student) =>
          student.id === studentId ? { ...student, leadStatus: nextStatus, leadStatusUpdatedAt: Timestamp.now() } : student
        )
      );
      logAdminActivity({
        action: 'Update Lead Status',
        details: `Lead status for ${studentName || 'student'} set to ${nextStatus}`,
        meta: { page: 'Applications', studentId, studentName: studentName || '', status: nextStatus }
      });
    } catch (error) {
      console.error('Failed to update lead status:', error);
      swal("Error", "Failed to update lead status. Please try again.", "error");
    }
  };

  const getNextLeadStatus = (current) => {
    const normalized = current || 'Neutral';
    const currentIndex = leadStatuses.indexOf(normalized);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % leadStatuses.length;
    return leadStatuses[nextIndex];
  };

  const uniqueCourses = Array.from(new Set(studentsList.map((student) => student.course).filter(Boolean))).sort();
  const uniqueCountries = Array.from(new Set(studentsList.map((student) => student.nationality).filter(Boolean))).sort();
  const filteredStudents = studentsList.filter((student) => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch = !search || [
      student.bio,
      student.course,
      student.nationality,
      student.phoneNumber
    ].some((value) => String(value || '').toLowerCase().includes(search));

    const matchesCourse = filterCourse === 'All' || student.course === filterCourse;
    const matchesCountry = filterCountry === 'All' || student.nationality === filterCountry;
    return matchesSearch && matchesCourse && matchesCountry;
  });

  const formatDateTime = (value) => {
    if (!value) return '—';
    const dateObj = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return '—';
    const datePart = dateObj.toLocaleDateString();
    const timePart = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${datePart} • ${timePart}`;
  };

  const totalCount = countAllData;
  const filteredCount = filteredStudents.length;
  const admissionsSnapshot = useMemo(() => {
    const now = new Date();
    const { start: weekStart, end: weekEnd } = getWeekBounds(now);
    const { start: monthStart, end: monthEnd } = getMonthBounds(now);
    const { start: yearStart, end: yearEnd } = getYearBounds(now);

    const applicantRows = allStudents
      .map((student) => ({
        ...student,
        applicationDate: getApplicationDate(student),
        admissionDate: getAdmissionDate(student)
      }))
      .filter((student) => student.applicationDate instanceof Date && !Number.isNaN(student.applicationDate.getTime()));

    const yearApplicants = applicantRows.filter((student) => student.applicationDate >= yearStart && student.applicationDate <= yearEnd);
    const yearAdmitted = yearApplicants.filter((student) => isJoinedStudent(student));
    const yearSuccessRate = yearApplicants.length > 0 ? (yearAdmitted.length / yearApplicants.length) * 100 : 0;
    const applicantsPerWeeklyTarget = yearSuccessRate > 0
      ? Math.ceil(WEEKLY_TARGET / (yearSuccessRate / 100))
      : null;
    const applicantsPerMonthlyTarget = yearSuccessRate > 0
      ? Math.ceil(MONTHLY_TARGET / (yearSuccessRate / 100))
      : null;

    const thisWeekAdmissions = applicantRows.filter((student) => (
      isJoinedStudent(student)
      && student.admissionDate >= weekStart
      && student.admissionDate <= weekEnd
    ));

    const thisMonthAdmissions = applicantRows.filter((student) => (
      isJoinedStudent(student)
      && student.admissionDate >= monthStart
      && student.admissionDate <= monthEnd
    ));

    return {
      thisWeekAdmissions: thisWeekAdmissions.length,
      thisMonthAdmissions: thisMonthAdmissions.length,
      yearApplicants: yearApplicants.length,
      yearAdmitted: yearAdmitted.length,
      yearSuccessRate,
      applicantsPerWeeklyTarget,
      applicantsPerMonthlyTarget,
      weeklyGap: Math.max(WEEKLY_TARGET - thisWeekAdmissions.length, 0),
      monthlyGap: Math.max(MONTHLY_TARGET - thisMonthAdmissions.length, 0)
    };
  }, [allStudents]);

  const admissionsGuidance = useMemo(() => {
    if (admissionsSnapshot.yearApplicants === 0) {
      return 'Once the application flow fills in, this snapshot will estimate how many applicants you need to stay on pace for the weekly and monthly targets.';
    }

    const weeklyApplicantsText = admissionsSnapshot.applicantsPerWeeklyTarget
      ? `about ${formatNumber(admissionsSnapshot.applicantsPerWeeklyTarget)} applicants`
      : 'more application data';
    const monthlyApplicantsText = admissionsSnapshot.applicantsPerMonthlyTarget
      ? `about ${formatNumber(admissionsSnapshot.applicantsPerMonthlyTarget)} applicants`
      : 'more application data';

    return `At the current-year conversion rate of ${formatPercent(admissionsSnapshot.yearSuccessRate)}, you need ${weeklyApplicantsText} to admit 5 students this week and ${monthlyApplicantsText} to admit 20 students this month.`;
  }, [admissionsSnapshot]);

  return (
    <div className="applicationsPageMinimal">
      <header className="applicationsMinimalHeader">
        <div>
          <span className="applicationsMinimalEyebrow">Admissions</span>
          <h1>Applications</h1>
          <p>Review prospective students and manage their application status.</p>
        </div>
      </header>

      <section className="applicationsOverview" aria-label="Admissions summary">
        <div className="applicationsMetric">
          <span>Applications</span>
          <strong>{loading ? <Spinner animation="border" role="status" /> : formatNumber(totalCount)}</strong>
        </div>
        <div className="applicationsMetric">
          <span>Admitted this week</span>
          <strong>{loading ? <Spinner animation="border" role="status" /> : formatNumber(admissionsSnapshot.thisWeekAdmissions)}</strong>
        </div>
        <div className="applicationsMetric">
          <span>Admitted this month</span>
          <strong>{loading ? <Spinner animation="border" role="status" /> : formatNumber(admissionsSnapshot.thisMonthAdmissions)}</strong>
        </div>
        <div className="applicationsMetric">
          <span>Year conversion</span>
          <strong>{loading ? <Spinner animation="border" role="status" /> : formatPercent(admissionsSnapshot.yearSuccessRate)}</strong>
        </div>
      </section>

      <div className="applicationsTargetNote">
        <FontAwesomeIcon icon={faBullseye} />
        <span><strong>Admissions targets:</strong> 5 per week and 20 per month. {loading ? 'Updating conversion guidance...' : admissionsGuidance}</span>
      </div>

      <section className="applicationsRegister">
        <div className="applicationsRegisterHeader">
          <div>
            <h2>Applicant register</h2>
            <p>Search applicants and filter by course or country.</p>
          </div>
          <span className="applicationsResultCount">{loading ? 'Loading…' : `${filteredCount} shown`}</span>
        </div>
        <div className="applicationsFilters">
          <Form.Control
            type="search"
            placeholder="Search name, course, or phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search applications"
          />
          <Form.Select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} aria-label="Filter by course">
            <option value="All">All courses</option>
            {uniqueCourses.map((course) => <option key={course} value={course}>{course}</option>)}
          </Form.Select>
          <Form.Select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} aria-label="Filter by country">
            <option value="All">All countries</option>
            {uniqueCountries.map((country) => <option key={country} value={country}>{country}</option>)}
          </Form.Select>
        </div>
        <div className="applicationsTableWrap">
          {loading ? (
            <div className="applicationsState"><Spinner animation="border" role="status" /><span>Loading applications...</span></div>
          ) : filteredStudents.length === 0 ? (
            <div className="applicationsEmpty">
              <h3>{studentsList.length === 0 ? 'No applications yet' : 'No matching applications'}</h3>
              <p>{studentsList.length === 0 ? 'New applicants will appear here.' : 'Try changing your search or filters.'}</p>
            </div>
          ) : (
          <Table hover className="applicationsTable">
            <thead>
              <tr>
                <th>Student</th>
                <th>Applied</th>
                <th>Course</th>
                <th>Country</th>
                <th>Phone</th>
                <th>Cohort</th>
                <th>Action</th>
                <th>Lead status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((mystudent) => (
                <tr key={mystudent.id}>
                  <td>
                    <div className="appsStudentCell">
                      <div className='appDefProfPic'>
                        <img src={mystudent.profilePic ? mystudent.profilePic : defaulProfPic} alt={mystudent.bio ? `${mystudent.bio} profile` : 'Student profile'} />
                      </div>
                      <div>
                        <p className="appsStudentName">{mystudent.bio || 'Unnamed student'}</p>
                      </div>
                    </div>
                  </td>
                  <td>{formatDateTime(mystudent.appDate)}</td>
                  <td>{mystudent.course || '—'}</td>
                  <td>{mystudent.nationality || '—'}</td>
                  <td>
                    <MaskedPhone
                      phoneNumber={mystudent.phoneNumber}
                      studentId={mystudent.userId}
                      studentName={mystudent.bio}
                    />
                  </td>
                  <td>{mystudent.cohort || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className='appsViewBtn'
                      onClick={() => viewStudent(mystudent.userId, mystudent.bio)}
                    >
                      View
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`leadStatusBtn leadStatusBtn--${normalizeLeadStatus(mystudent.leadStatus)}`}
                      onClick={() =>
                        updateLeadStatus(mystudent.id, mystudent.bio, getNextLeadStatus(mystudent.leadStatus))
                      }
                    >
                      {mystudent.leadStatus || 'Neutral'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          )}
        </div>
      </section>
    </div>

  )
}

export default AppComp
